import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { inject, Service } from '@angular/core';
import { StateService } from '@adgate/common/services/state-service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, tap } from 'rxjs';
import { environment } from '@env/environment';
import { BaseService } from '@adgate/common/services/base-service';

@Service()
export class AuthenticationService extends BaseService
{
  public TOKENSTATUS_VALID: number         = 1;
  public TOKENSTATUS_EXPIRED: number       = 2;
  public TOKENSTATUS_NEEDSREFRESH: number  = 3;

  private REFRESHTOKEN_THRESHOLD: number    = 10 * 60 * 1000; // 10 minutes in milliseconds

  private stateService: StateService = inject(StateService) ;
  
  isUserAuthenticated()
  {
    return (this.stateService.data.userToken != null && !this.tokenExpired(this.stateService.data.userToken));
  }

  tokenExpired(token: string)
  {
    var jwtDecoder = new JwtHelperService();
    return jwtDecoder.isTokenExpired(token);
  }

  getTokenStatus(token: string): number
  {
    var jwtDecoder = new JwtHelperService();
    var tokenExpirationDate = jwtDecoder.getTokenExpirationDate(token);
    var now = new Date();
    var timeDiff = tokenExpirationDate!.getTime() - now.getTime();

    if ( timeDiff <= 0 ) {
      return this.TOKENSTATUS_EXPIRED;
    } else if ( timeDiff <= this.REFRESHTOKEN_THRESHOLD ) {
      return this.TOKENSTATUS_NEEDSREFRESH;
    } else {
      return this.TOKENSTATUS_VALID;
    }
  }

  decodeToken(token: string)
  {
    var jwtDecoder = new JwtHelperService();
    var decoded = jwtDecoder.decodeToken(token);
    return decoded;
  }

  checkPrivilege(privs: number[]): boolean
  {
    let privFound = false;
    let decodedToken = this.decodeToken(this.stateService.data.userToken);
    let tokenData = JSON.parse(decodedToken.data);

    for (let i: number = 0; i < privs.length; i++ ) {
      for (let j: number = 0; j < tokenData.privileges.length ; j++) {
        if ( privs[i] == tokenData.privileges[j].id ) {
          privFound = true;
          break;
        }
      }
    }
      
    return privFound;
  }

  checkPrivilegeOnToken(decodedToken: any, privs: number[]): boolean
  {
    let privFound = false;
    let tokenData = JSON.parse(decodedToken.data);
      
    for (let i: number = 0; i < privs.length; i++ ) {
      for (let j: number = 0; j < tokenData.privileges.length ; j++) {
        if ( privs[i] == tokenData.privileges[j].id ) {
          privFound = true;
          break;
        }
      }
    }
    
    return privFound;
  }

  signOut(): void
  {
    this.stateService.init();
    this.stateService.navigate('/login');
  }

  authenticate(login: string, password: string): Observable<RESTApplicationResponse>
  {
    let loginData = {
        login: login,
        password: password
    }

    let url = `${environment.baseServiceUrl}auth/authenticate`;
    return this.sendRequest(url, loginData, 'post').pipe(
      tap((r:RESTApplicationResponse) => {
        let decodedToken = this.decodeToken(r.data.authToken);
        let tokenData = JSON.parse(decodedToken.data);
        this.stateService.data.userToken = r.data.authToken;
        this.stateService.data.refreshToken = r.data.refreshToken;
        this.stateService.data.userId = tokenData.userId;
        this.stateService.data.userName = tokenData.fullName;
      })
    );
  }

  authenticateMyId( code: string, codeChallenge: string  ): Observable<RESTApplicationResponse>
  {
    let loginData = {
        code: code,
        codechallenge: codeChallenge
    }

    let url = `${environment.baseServiceUrl}auth/authenticateMyid`;
    return this.sendRequest(url, loginData, 'post').pipe(
      tap((r:RESTApplicationResponse) => {
        let decodedToken = this.decodeToken(r.data.authToken);
        let tokenData = JSON.parse(decodedToken.data);
        this.stateService.data.userToken = r.data.authToken;
        this.stateService.data.refreshToken = r.data.refreshToken;
        this.stateService.data.userId = tokenData.userId;
        this.stateService.data.userName = tokenData.fullName;
      })
    );
  }

  refreshAuthenticationToken()
  {
    let url = `${environment.baseServiceUrl}auth/refreshAuthenticationToken`;
    return this.sendRequest(url, this.stateService.data.refreshToken, 'post').pipe(
      tap((r:RESTApplicationResponse) => {
        let decodedToken = this.decodeToken(r.data.authToken);
        let tokenData = JSON.parse(decodedToken.data);
        this.stateService.data.userToken = r.data.authToken;
        this.stateService.data.refreshToken = r.data.refreshToken;
        this.stateService.data.userId = tokenData.userId;
        this.stateService.data.userName = tokenData.fullName;
      })
    );
  }

  getUserAttributes()
  {
    var decodedToken = this.decodeToken(this.stateService.data.userToken);
    let tokenData = JSON.parse(decodedToken.data);
    return tokenData.attributes;
  }

  updateUserPassword(currentPassword: string, newPassword: string)
  {
    let data = {
      email: this.getUserAttributes().email,
      currentPassword: currentPassword,
      newPassword: newPassword
    }

    let url = environment.baseServiceUrl + "admin/updateUserPassword";
    return this.sendRequest(url, data, 'post');
  }

  startPasswordRecovery( login: string )
  {
    let payload = {
      login: login,
    }

    let url = `${environment.baseServiceUrl}auth/startPasswordRecovery`;
    return this.sendRequest(url, payload, 'post' );
  }

  recoverPassword( recoveryCode: string, newPassword: string )
  {
    let payload = {
      recoveryCode: recoveryCode,
      password: newPassword
    }

    let url = `${environment.baseServiceUrl}auth/recoverPassword`;
    return this.sendRequest(url, payload, 'post' );
  }
}
